// web/src/pages/Data.tsx
import type { JSX, ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const EFFECTIVE_DATE_TEXT = "August 21, 2025";
const LAST_UPDATED_TEXT = "August 21, 2025";
const SUPPORT_EMAIL = "support@spellgather.com";

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-8 mb-3">
        {title}
      </h2>
      <div className="prose prose-invert max-w-none">{children}</div>
    </section>
  );
}

export default function Data(): JSX.Element {
  return (
    <main className="mx-auto max-w-4xl p-6">
      <Card className="bg-gray-900 border-gray-700 shadow-xl">
        <CardHeader>
          <CardTitle className="text-white">
            Data Sharing &amp; Protection Statement
          </CardTitle>
          <CardDescription className="text-white/90">
            Effective: {EFFECTIVE_DATE_TEXT} · Last Updated: {LAST_UPDATED_TEXT}
          </CardDescription>
        </CardHeader>

        <CardContent className="text-white">
          {/* TOC */}
          <nav aria-label="Table of contents" className="mb-6">
            <ul className="text-sm grid sm:grid-cols-2 gap-2">
              <li>
                <a className="underline" href="#scope">
                  1. Scope & Relationship to Other Policies
                </a>
              </li>
              <li>
                <a className="underline" href="#data-we-collect">
                  2. Data We Collect
                </a>
              </li>
              <li>
                <a className="underline" href="#how-we-use">
                  3. How We Use Data (Purposes & Bases)
                </a>
              </li>
              <li>
                <a className="underline" href="#sharing">
                  4. When We Share Data
                </a>
              </li>
              <li>
                <a className="underline" href="#no-sale">
                  5. No “Sale” of Personal Data
                </a>
              </li>
              <li>
                <a className="underline" href="#cookies">
                  6. Cookies & Similar Technologies
                </a>
              </li>
              <li>
                <a className="underline" href="#security">
                  7. Security Measures
                </a>
              </li>
              <li>
                <a className="underline" href="#retention">
                  8. Retention
                </a>
              </li>
              <li>
                <a className="underline" href="#transfers">
                  9. International Transfers
                </a>
              </li>
              <li>
                <a className="underline" href="#your-rights">
                  10. Your Rights & Controls
                </a>
              </li>
              <li>
                <a className="underline" href="#minors">
                  11. Children & Teens
                </a>
              </li>
              <li>
                <a className="underline" href="#automations">
                  12. Automated Decisions & Profiling
                </a>
              </li>
              <li>
                <a className="underline" href="#incidents">
                  13. Incident Response
                </a>
              </li>
              <li>
                <a className="underline" href="#subprocessors">
                  14. Current Sub-Processors
                </a>
              </li>
              <li>
                <a className="underline" href="#changes">
                  15. Changes to This Statement
                </a>
              </li>
              <li>
                <a className="underline" href="#contact">
                  Contact
                </a>
              </li>
            </ul>
          </nav>

          <Section id="scope" title="1) Scope & Relationship to Other Policies">
            <p>
              This Data Sharing &amp; Protection Statement explains how
              Spellgather handles personal information, the limited
              circumstances in which we share it, and the safeguards we apply.
              It complements our{" "}
              <Link className="underline" to="/privacy">
                Privacy Policy
              </Link>{" "}
              and{" "}
              <Link className="underline" to="/terms">
                Terms of Service
              </Link>
              . If there’s a conflict, the Privacy Policy controls on data
              handling and your rights.
            </p>
          </Section>

          <Section id="data-we-collect" title="2) Data We Collect">
            <ul>
              <li>
                <strong>Account &amp; Authentication:</strong> Email, display
                name, username, avatar URL, login timestamps, and authentication
                provider metadata.
              </li>
              <li>
                <strong>Profile &amp; Preferences:</strong> Bio, location
                visibility, play/trade availability, formats, commander
                brackets, meet-up preferences, and preferred LGS.
              </li>
              <li>
                <strong>Content &amp; Activity:</strong> Collection entries
                (including images), wishlist, messages, offers,
                follows/followers, notifications, and item history markers
                (e.g., “added to collection,” “traded to @user,” with
                timestamps).
              </li>
              <li>
                <strong>Technical:</strong> Device, browser, IP (at access
                time), coarse geolocation when you opt in, and logs required for
                security and fraud prevention.
              </li>
              <li>
                <strong>Support:</strong> Information you send to{" "}
                <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>{" "}
                for help or reports.
              </li>
            </ul>
          </Section>

          <Section
            id="how-we-use"
            title="3) How We Use Data (Purposes & Legal Bases)"
          >
            <p className="mb-2">We process data to:</p>
            <ul>
              <li>
                Provide and secure the Service (account login, messaging,
                offers, collections).
              </li>
              <li>
                Enable discovery and community features (profiles, follows,
                marketplace visibility).
              </li>
              <li>
                Combat spam, fraud, and abuse; enforce{" "}
                <Link className="underline" to="/community">
                  Community Guidelines
                </Link>
                .
              </li>
              <li>
                Communicate about account activity (e.g., offers, follows, DMs)
                when you opt in to email notifications.
              </li>
              <li>
                Improve features and reliability through aggregate analytics.
              </li>
            </ul>
            <p className="mt-2">
              <strong>Legal bases (where applicable):</strong> contract (to
              provide the Service), legitimate interests (security, abuse
              prevention, service improvement), consent (optional email
              notifications, location), and legal obligation (record-keeping
              when required).
            </p>
          </Section>

          <Section id="sharing" title="4) When We Share Data">
            <p className="mb-2">
              We share personal data only as needed to operate the Service or
              when required by law:
            </p>
            <ul>
              <li>
                <strong>Service Providers (Processors):</strong> We use
                reputable vendors under written contracts and data protection
                terms to deliver core functions (hosting, storage, auth, email).
              </li>
              <li>
                <strong>Legal &amp; Safety:</strong> We may disclose information
                to comply with valid legal requests, enforce our terms, or
                protect users and the public from harm, fraud, or security
                threats.
              </li>
              <li>
                <strong>Business Changes:</strong> If we undergo a merger,
                acquisition, or asset transfer, your information may be
                transferred subject to this Statement (or you’ll be notified of
                materially different practices).
              </li>
            </ul>
            <p className="mt-2">
              We do <em>not</em> share personal data with third-party
              advertisers for their independent use.
            </p>
          </Section>

          <Section id="no-sale" title="5) No “Sale” of Personal Data">
            <p>
              Spellgather does not “sell” personal information as defined by
              applicable privacy laws, nor do we permit cross-context behavioral
              advertising using your personal data.
            </p>
          </Section>

          <Section id="cookies" title="6) Cookies &amp; Similar Technologies">
            <p>
              We use strictly necessary cookies and local storage for login
              sessions and security. If we later add analytics with cookies, we
              will update this page and provide controls to manage your consent
              where required.
            </p>
          </Section>

          <Section id="security" title="7) Security Measures">
            <ul>
              <li>
                <strong>Encryption:</strong> TLS in transit and provider-managed
                encryption at rest.
              </li>
              <li>
                <strong>Access Controls:</strong> Role-based, least-privilege
                access to production systems and data.
              </li>
              <li>
                <strong>Isolation:</strong> Logical isolation of tenant data via
                application and Firestore security rules.
              </li>
              <li>
                <strong>Hardening &amp; Monitoring:</strong> Provider-level DDoS
                protections and audit logs for critical actions.
              </li>
              <li>
                <strong>Secure Development:</strong> Review of sensitive code
                paths, dependency updates, and key rotation.
              </li>
            </ul>
            <p className="mt-2">
              No internet service is 100% secure, but we continuously improve
              our safeguards. Report security issues to{" "}
              <a
                className="underline"
                href={`mailto:${SUPPORT_EMAIL}?subject=Security%20Report`}
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section id="retention" title="8) Retention">
            <p>
              We retain personal data for as long as needed to provide the
              Service and for legitimate business purposes (e.g., security,
              fraud prevention, record-keeping), then delete or de-identify it.
              Typical examples:
            </p>
            <ul>
              <li>
                Account data: kept while your account is active; deleted upon
                verified request or account deletion.
              </li>
              <li>
                Messages &amp; offers: retained while your account is active,
                subject to user deletion and legal holds.
              </li>
              <li>
                Logs &amp; security artifacts: kept for a limited period to
                investigate abuse and ensure integrity.
              </li>
              <li>
                Collection item history markers: retained to preserve
                on-platform provenance. If you delete your account, personal
                identifiers are removed or pseudonymized where feasible.
              </li>
            </ul>
          </Section>

          <Section id="transfers" title="9) International Transfers">
            <p>
              We may store or process data in countries other than your own.
              Where required, we rely on appropriate transfer mechanisms (e.g.,
              Standard Contractual Clauses) and vendor commitments to protect
              your information to a comparable standard.
            </p>
          </Section>

          <Section id="your-rights" title="10) Your Rights &amp; Controls">
            <p className="mb-2">
              Depending on your location, you may have rights to access,
              correct, delete, restrict, or object to processing of your
              personal data, and to receive a portable copy.
            </p>
            <ul>
              <li>
                <strong>In-App:</strong> Manage visibility and preferences in{" "}
                <Link to="/settings" className="underline">
                  Settings
                </Link>{" "}
                (e.g., location, formats, meet-ups, LGS, notifications).
              </li>
              <li>
                <strong>Export or Delete:</strong> Request a data export or
                account deletion by emailing{" "}
                <a
                  className="underline"
                  href={`mailto:${SUPPORT_EMAIL}?subject=Data%20Request`}
                >
                  {SUPPORT_EMAIL}
                </a>
                . We’ll verify your identity and respond within applicable
                timelines.
              </li>
              <li>
                <strong>Email Preferences:</strong> Opt in/out of emails
                (offers, follows, messages) in{" "}
                <Link to="/settings" className="underline">
                  Settings
                </Link>
                .
              </li>
            </ul>
            <p className="mt-2">
              If you believe your rights were not respected, you may appeal to
              us at the same address or contact your local data protection
              authority.
            </p>
          </Section>

          <Section id="minors" title="11) Children &amp; Teens">
            <p>
              Spellgather is not directed to children under 13. Teens aged 13–17
              may use the Service with parent/guardian consent and supervision.
              We apply additional safety expectations described in our{" "}
              <Link className="underline" to="/community">
                Community Guidelines
              </Link>
              . Guardians may contact us at{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>{" "}
              to manage a teen’s account or request data actions.
            </p>
          </Section>

          <Section
            id="automations"
            title="12) Automated Decisions &amp; Profiling"
          >
            <p>
              We do not engage in solely automated decisions that have legal or
              similarly significant effects on users. Basic automations (e.g.,
              spam/abuse heuristics, notification delivery) are used to protect
              the Service and provide requested features.
            </p>
          </Section>

          <Section id="incidents" title="13) Incident Response">
            <p>
              We maintain processes to detect, assess, and respond to security
              incidents. If we determine that a breach creates a risk of harm,
              we will notify affected users and/or regulators as required by
              law.
            </p>
          </Section>

          <Section id="subprocessors" title="14) Current Sub-Processors">
            <p className="mb-2">
              We use the following primary service providers to deliver
              Spellgather (subject to change):
            </p>
            <ul>
              <li>
                <strong>
                  Google Firebase (Authentication, Firestore, Storage):
                </strong>{" "}
                hosting, auth, database, and file storage.
              </li>
              <li>
                <strong>Transactional Email Provider:</strong> to send account
                notifications you opt in to (e.g., SendGrid/Mailgun or a
                comparable provider).
              </li>
            </ul>
            <p className="mt-2">
              We will update this section when we materially change
              sub-processors.
            </p>
          </Section>

          <Section id="changes" title="15) Changes to This Statement">
            <p>
              We may update this Statement to reflect new features, vendors, or
              legal requirements. Material changes will be announced in-app
              and/or by email when appropriate. Continued use after updates
              constitutes acceptance of the revised Statement.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              Questions or requests? Email{" "}
              <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <p className="mt-2">
              Also see our{" "}
              <Link to="/privacy" className="underline">
                Privacy Policy
              </Link>
              ,{" "}
              <Link to="/terms" className="underline">
                Terms of Service
              </Link>
              , and{" "}
              <Link to="/community" className="underline">
                Community Guidelines
              </Link>
              .
            </p>
          </Section>

          {/* Convenience CTAs */}
          <div className="mt-8 flex flex-wrap gap-2">
            <Link to="/settings">
              <Button className="bg-purple-600 hover:bg-purple-700">
                Open Settings
              </Button>
            </Link>
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Data%20Export%20Request`}>
              <Button
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
              >
                Request Data Export
              </Button>
            </a>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Account%20Deletion%20Request`}
            >
              <Button
                variant="outline"
                className="border-slate-600 text-slate-200 hover:bg-slate-800"
              >
                Request Account Deletion
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

